import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/context/LanguageContext";
import { PageHero } from "@/components/common/PageHero";
import { CTASection } from "@/components/home/CTASection";
import { ArrowLeft, CheckCircle, Quotes, MapPin, Buildings } from "@phosphor-icons/react";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const { t, lang } = useT();
  const [cs, setCs] = useState(null);

  useEffect(() => {
    api.get(`/case-studies/${slug}`).then((r) => setCs(r.data)).catch(() => setCs(false));
  }, [slug, lang]);

  if (cs === false) return <div className="pt-40 pb-24 text-center text-slate-500">Case study not found.</div>;
  if (!cs) return <div className="pt-40 pb-24 text-center text-slate-400">Loading…</div>;

  return (
    <div data-testid="case-study-detail-page">
      <PageHero
        overline={`${cs.industry} · ${cs.country}`}
        title={cs.title}
        subtitle={cs.summary}
        crumbs={[{ label: t("caseStudies.overline"), to: "/case-studies" }, { label: cs.client }]}
        image={cs.cover_image}
      />

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-slate-100 pb-8 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><Buildings size={16} className="text-teal" /> {cs.client}</span>
            <span className="flex items-center gap-1.5"><MapPin size={16} className="text-teal" /> {cs.country}</span>
            <span className="rounded bg-teal/10 px-2 py-0.5 text-xs font-semibold text-teal">{cs.industry}</span>
          </div>

          {(cs.metrics || []).length > 0 && (
            <div className="mt-10 grid gap-4 sm:grid-cols-3" data-testid="case-study-metrics">
              {cs.metrics.map((m) => (
                <div key={m.label} className="rounded-xl bg-gradient-to-br from-[#0A192F] to-[#12365c] p-6 text-center">
                  <p className="font-heading text-3xl font-bold text-white">{m.value}</p>
                  <p className="mt-1 text-xs text-slate-300">{m.label}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-14 grid gap-10 lg:grid-cols-2">
            <div data-testid="case-study-challenge">
              <p className="text-xs uppercase tracking-[0.3em] font-semibold text-teal">{t("caseStudies.challenge")}</p>
              <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{cs.challenge}</p>
            </div>
            <div data-testid="case-study-approach">
              <p className="text-xs uppercase tracking-[0.3em] font-semibold text-teal">{t("caseStudies.approach")}</p>
              <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{cs.approach}</p>
            </div>
          </div>

          <div className="mt-14" data-testid="case-study-results">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-teal">{t("caseStudies.results")}</p>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {(cs.results || []).map((r) => (
                <li key={r.slice(0, 30)} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-mist/40 p-4 text-sm leading-relaxed text-slate-700">
                  <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-teal" /> {r}
                </li>
              ))}
            </ul>
          </div>

          {cs.quote && (
            <figure className="mt-14 rounded-xl border border-teal/30 bg-gradient-to-r from-[#0A192F] to-[#12365c] p-8 lg:p-10" data-testid="case-study-quote">
              <Quotes size={36} weight="fill" className="text-teal-light/40" />
              <blockquote className="mt-4 font-heading text-xl leading-relaxed text-white lg:text-2xl">"{cs.quote}"</blockquote>
              <figcaption className="mt-5 text-sm font-semibold text-teal-light">{cs.quote_author}</figcaption>
            </figure>
          )}

          <Link to="/case-studies" className="mt-12 inline-flex items-center gap-2 text-sm font-semibold text-teal hover:underline" data-testid="case-study-back">
            <ArrowLeft size={16} weight="bold" /> {t("caseStudies.back")}
          </Link>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
